import React, { useCallback, useEffect, useMemo } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useTranslate } from "hooks";
import { Button } from "components/Button";
import s from "./ConfiguratorModal.module.scss";
import {
  useActivePartKey,
  useProductOutput,
  useActiveTab,
  useProductActions,
  resetProductUIStore,
  useIsWizardProduct,
  ConfiguratorTabs,
  useSetActivePartKey,
  useIsProductLoading,
} from "stores/product";
import { OrderCodeForm } from "components/ProductConfigurator/components/OrderCodeForm/OrderCodeForm";
import { ProductConfiguratorHeader } from "components/ProductConfigurator/components/Header/ProductConfiguratorHeader";
import type {
  LoadWizardNode,
  LoadWizardProduct,
} from "stores/product/product/productActions";
import { XCircleLoader } from "components/XCircleLoader/XCircleLoader";
import { ModalTechnicalProperties } from "./ModalTechnicalProperties";
import { UpdateConfigurationButton } from "./UpdateConfigurationButton";
import { toast } from "react-toastify";

export const ProductConfiguratorModal = NiceModal.create(
  Scrivito.connect(
    ({
      wizardArgs,
      forceLoadDefaultWizardProduct = false,
      cleanupOnClose = false,
      handleBack: handleBackAction,
    }: {
      wizardArgs?: LoadWizardNode | LoadWizardProduct;
      forceLoadDefaultWizardProduct?: boolean;
      cleanupOnClose?: boolean;
      handleBack?: () => void;
    }) => {
      const translate = useTranslate();
      const modal = useModal();
      const productOutput = useProductOutput();
      const setActivePartKey = useSetActivePartKey();
      const activePartKey = useActivePartKey();
      const isProductLoading = useIsProductLoading();
      const activeTab = useActiveTab();
      const productActions = useProductActions();
      const isWizardProduct = useIsWizardProduct();

      const handleOpened = useCallback(() => {
        if (activePartKey) {
          const modalPartElement = document.getElementById(
            `modal-part-${activePartKey}`,
          );
          modalPartElement?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, [activePartKey]);

      const handleClose = useCallback(() => {
        modal.resolve();
        modal.hide();
        if (cleanupOnClose) {
          productActions.clear();
          resetProductUIStore();
        }
        setActivePartKey(null);
      }, [modal, cleanupOnClose, productActions, setActivePartKey]);

      const handleBack = useCallback(() => {
        if (handleBackAction) {
          handleClose();
          handleBackAction?.();
        }
      }, [handleBackAction, handleClose]);

      const isMinimalContent = useMemo(() => {
        const parts = productOutput?.product?.orderCode?.parts ?? [];
        const result = parts.filter((part) => {
          return (
            (part?.state !== null &&
              part?.state !== undefined &&
              part?.state < 2) ||
            (part?.state === 3 && part?.presentation)
          );
        });
        return (
          result.some(
            (res) => res?.options?.length && res?.options?.length > 1,
          ) || result.length > 2
        );
      }, [productOutput]);

      useEffect(() => {
        if (!wizardArgs) {
          return;
        }
        if (!("nodeId" in wizardArgs)) {
          toast.error("Wizard node ID is not available");
          return;
        }
        if (!forceLoadDefaultWizardProduct) {
          productActions.loadWizardNode(wizardArgs);
        } else {
          const { nodeId: _nodeId, ...req } = wizardArgs;
          productActions.loadWizardProduct(req);
        }
      }, [productActions, wizardArgs, forceLoadDefaultWizardProduct]);

      return (
        <Modal
          isOpen={modal.visible}
          onClosed={handleClose}
          toggle={handleClose}
          size="xl"
          contentClassName="h-100"
          onOpened={handleOpened}
        >
          <div
            className={cx("d-flex flex-column h-100", s.configuratorModalInner)}
          >
            {isProductLoading && !productOutput ? (
              <XCircleLoader />
            ) : (
              <>
                <ModalHeader
                  toggle={handleClose}
                  tag="div"
                  className="align-items-start pb-0 bg-white"
                >
                  {modal.visible && handleBackAction ? (
                    <Button
                      data-testid="ProductConfiguratorModal_back-btn"
                      color="default"
                      className="px-0 mb-1"
                      icon="chevron-left-regular"
                      onClick={handleBack}
                    >
                      {translate("BACK")}
                    </Button>
                  ) : null}
                </ModalHeader>

                <ModalBody
                  className={cx("flex-grow-1  scroll-smooth", {
                    "overflow-auto": isMinimalContent,
                  })}
                >
                  {modal.visible && (
                    <div className="px-5 h-100 d-flex flex-column">
                      <div className="flex-shrink-0">
                        <ProductConfiguratorHeader isModal />
                      </div>
                      <div className="flex-grow-1 overflow-y-auto">
                        {productOutput?.product?.orderCode &&
                        (!isWizardProduct ||
                          activeTab ===
                            ConfiguratorTabs.PRODUCT_SPECIFICATION) ? (
                          <OrderCodeForm isModal />
                        ) : (
                          <ModalTechnicalProperties />
                        )}
                      </div>
                    </div>
                  )}
                </ModalBody>
                <ModalFooter className="d-flex align-items-center p-4 pb-2 w-100 bg-white">
                  {isWizardProduct ? (
                    <UpdateConfigurationButton
                      removeModal={handleClose}
                      nodeId={
                        wizardArgs && "nodeId" in wizardArgs
                          ? wizardArgs.nodeId
                          : undefined
                      }
                    />
                  ) : (
                    <Button
                      data-testid="ProductConfiguratorModal_update-configuration-btn"
                      className="m-0"
                      onClick={handleClose}
                    >
                      {translate("PRODUCT_CONFIGURATOR_DONE")}
                    </Button>
                  )}
                </ModalFooter>
              </>
            )}
          </div>
        </Modal>
      );
    },
  ),
);
